import { motion } from "framer-motion";
import { Sparkles, Target, Handshake } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1748256622734-92241ae7b43f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBIUiUyMHRlYW0lMjBzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwb2ZmaWNlfGVufDB8fHx8MTc5MDI1NTE4Mnww&ixlib=rb-4.1.0&q=85";

const HIGHLIGHTS = [
  { icon: Sparkles, title: "Clarity", text: "Transparent processes and honest guidance at every step." },
  { icon: Target, title: "Capability", text: "Deep HR expertise paired with strong engineering talent." },
  { icon: Handshake, title: "Creativity", text: "Tailored solutions designed around your business goals." },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute top-1/3 -right-40 w-[420px] h-[420px] rounded-full bg-[#F89335]/8 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative order-2 lg:order-1"
        >
          <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/40">
            <img src={ABOUT_IMG} alt="Krijo team collaborating in a modern office" className="w-full h-[320px] sm:h-[440px] object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#010433]/70 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-6 left-6 glass-card rounded-2xl px-6 py-4">
            <p className="font-['Poppins'] font-bold text-2xl text-gradient-gold">2-in-1</p>
            <p className="text-xs text-slate-400 mt-1">HR + Technology under one roof</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 32 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="order-1 lg:order-2"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#F89335]">About Us</p>
          <h2 data-testid="about-title" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Welcome to <span className="text-gradient-gold">Krijo Consultancy Services</span>
          </h2>
          <p data-testid="about-content" className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed">
            Krijo Consultancy Services helps businesses grow through strategic HR solutions and innovative software services. We combine people expertise with technology-driven solutions to help organizations scale efficiently.
          </p>

          <div className="mt-10 space-y-5">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div
                key={h.title}
                data-testid={`about-highlight-${h.title.toLowerCase()}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                className="glass-card rounded-2xl p-5 flex items-start gap-4"
              >
                <span className="w-11 h-11 shrink-0 rounded-xl bg-[#F89335]/15 flex items-center justify-center">
                  <h.icon size={20} className="text-[#F89335]" />
                </span>
                <div>
                  <h3 className="font-['Poppins'] font-semibold text-lg">{h.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{h.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
